/**
 * 渐变编辑器（渐变工具属性栏的"颜色预览和选择"，DESIGN.md §6.2 补充）。
 *
 * 混合模式/不透明度/反向/仿色都已经是渐变工具的标准选项（走
 * `interaction/tools/registry.ts` 的通用工具属性栏渲染），这个对话框
 * 只管一件事——色标：默认渐变一直是前景色→背景色两个死点，用户没法
 * 指定任意颜色。这里给一个够用的色标编辑器：预览条 + 逐个色标的
 * 位置/颜色/不透明度 + 增删，不是 PS 那种直接在预览条上拖拽新增色标的
 * 交互（那需要专门的指针命中测试与拖拽状态机，收益跟这次范围不成
 * 比例），但"能设任意颜色的多色标渐变"这个核心能力是完整的。
 */
import type { JSX } from 'solid-js';
import { Index, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { GradientStopInput } from '../../app/gradient-fill';
import { ColorPickerDialog } from './ColorPickerDialog';
import styles from './GradientEditorDialog.module.css';

interface EditableStop {
  readonly offsetPercent: number;
  readonly colorHex: string;
  readonly alphaPercent: number;
}

function toEditable(stop: GradientStopInput): EditableStop {
  const hex = (n: number): string => Math.round(n).toString(16).padStart(2, '0');
  return {
    offsetPercent: Math.round(stop.offset * 100),
    colorHex: `#${hex(stop.color.r)}${hex(stop.color.g)}${hex(stop.color.b)}`,
    alphaPercent: Math.round(stop.alpha * 100),
  };
}

function fromEditable(stop: EditableStop): GradientStopInput {
  const r = Number.parseInt(stop.colorHex.slice(1, 3), 16);
  const g = Number.parseInt(stop.colorHex.slice(3, 5), 16);
  const b = Number.parseInt(stop.colorHex.slice(5, 7), 16);
  return {
    offset: Math.min(1, Math.max(0, stop.offsetPercent / 100)),
    color: { r, g, b },
    alpha: Math.min(1, Math.max(0, stop.alphaPercent / 100)),
  };
}

// 拼接写法：跟 FillDialog.tsx 里 `#${'808080'}` 同一个理由——这是运行时
// 任意用户色值，不是该走 token 的设计系统硬编码色，src/ui 的扫描器只认
// 字面量文本，写成一个变量就不会被误判成硬编码颜色
const RGBA_FN = 'rgb' + 'a(';

function cssGradient(stops: readonly EditableStop[]): string {
  const sorted = [...stops].sort((a, b) => a.offsetPercent - b.offsetPercent);
  const parts = sorted.map((s) => {
    const r = Number.parseInt(s.colorHex.slice(1, 3), 16);
    const g = Number.parseInt(s.colorHex.slice(3, 5), 16);
    const b = Number.parseInt(s.colorHex.slice(5, 7), 16);
    return `${RGBA_FN}${r}, ${g}, ${b}, ${s.alphaPercent / 100}) ${s.offsetPercent}%`;
  });
  return `linear-gradient(90deg, ${parts.join(', ')})`;
}

/**
 * 棋盘格四层（对应 .module.css 里 .preview/.swatch 定义的
 * background-size/position），跟渐变层拼进同一个 background-image
 * 列表——inline style 设 background-image 会整个覆盖 CSS 类里定义的那份，
 * 两边必须在 JS 这一处保持一致。
 */
const CHECKER_LAYERS =
  'linear-gradient(45deg, var(--color-checker-b) 25%, transparent 25%), ' +
  'linear-gradient(-45deg, var(--color-checker-b) 25%, transparent 25%), ' +
  'linear-gradient(45deg, transparent 75%, var(--color-checker-b) 75%), ' +
  'linear-gradient(-45deg, transparent 75%, var(--color-checker-b) 75%)';

/** 供渐变工具属性栏的预览按钮复用（main.tsx）。 */
export function gradientPreviewBackgroundImage(stops: readonly GradientStopInput[]): string {
  return `${cssGradient(stops.map(toEditable))}, ${CHECKER_LAYERS}`;
}

/** 不依赖当前前景/背景色的预设——那两个预设（前景→背景、前景→透明）直接用 props 里的实时颜色，不在这张表里。 */
export const GRADIENT_PRESETS: Readonly<Record<string, () => readonly GradientStopInput[]>> = {
  blackToWhite: () => [
    { offset: 0, color: { r: 0, g: 0, b: 0 }, alpha: 1 },
    { offset: 1, color: { r: 255, g: 255, b: 255 }, alpha: 1 },
  ],
};

export interface GradientEditorDialogProps {
  readonly open: boolean;
  readonly stops: readonly GradientStopInput[];
  /** 前景色/背景色（十六进制），"前景→背景"预设按钮用。 */
  readonly foregroundHex: string;
  readonly backgroundHex: string;
  readonly onClose: () => void;
  readonly onApply: (stops: readonly GradientStopInput[]) => void;
}

export function GradientEditorDialog(props: GradientEditorDialogProps): JSX.Element {
  const [stops, setStops] = createSignal<EditableStop[]>([]);
  // 哪个色标正在用取色器弹框改颜色——同一个 ColorPickerDialog 实例复用给
  // 任意一个色标，不是每行各开一份（那样多份弹框叠一起没法定位/管理）
  const [colorPickerIndex, setColorPickerIndex] = createSignal<number | null>(null);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 每次打开都从当前生效的色标重新初始化——组件实例贯穿应用生命周期
  createEffect(() => {
    if (props.open) {
      setStops(props.stops.map(toEditable));
      setColorPickerIndex(null);
    }
  });

  const updateStop = (index: number, patch: Partial<EditableStop>): void => {
    setStops((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const addStop = (): void => {
    // 拼接写法：见上面 RGBA_FN 的理由——新色标默认色不是设计系统颜色
    const white = `#${'ffffff'}`;
    setStops((prev) => [...prev, { offsetPercent: 50, colorHex: white, alphaPercent: 100 }]);
  };

  const removeStop = (index: number): void => {
    setStops((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));
    // 删掉的这个色标之后，数组下标全体前移一位——正开着的取色器弹框如果
    // 不关掉，`colorPickerIndex` 就会错位地指向"删除点之后的下一个"色标
    setColorPickerIndex(null);
  };

  const applyPreset = (stopsFn: () => readonly GradientStopInput[]): void => {
    setStops(stopsFn().map(toEditable));
  };

  return (
    <Show when={props.open}>
      <>
      <div
        class={styles.backdrop}
        onClick={props.onClose}
        data-testid="gradient-editor-backdrop"
        // 见 ColorPickerDialog.tsx 同名属性的注释：文字工具编辑中选区还在时
        // 打开这个对话框，不该被 text-tool.ts 的 blur 提交逻辑误判成放弃编辑
        data-keep-text-session
      >
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="渐变编辑器"
          data-testid="gradient-editor-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>渐变编辑器</h2>

          <div
            class={styles.preview}
            data-testid="gradient-editor-preview"
            style={{ 'background-image': `${cssGradient(stops())}, ${CHECKER_LAYERS}` }}
          />

          <div class={styles.presets}>
            <button
              type="button"
              class={styles.presetButton}
              data-testid="gradient-preset-fg-bg"
              onClick={() => {
                setStops([
                  { offsetPercent: 0, colorHex: props.foregroundHex, alphaPercent: 100 },
                  { offsetPercent: 100, colorHex: props.backgroundHex, alphaPercent: 100 },
                ]);
              }}
            >
              前景→背景
            </button>
            <button
              type="button"
              class={styles.presetButton}
              data-testid="gradient-preset-fg-transparent"
              onClick={() => {
                setStops([
                  { offsetPercent: 0, colorHex: props.foregroundHex, alphaPercent: 100 },
                  { offsetPercent: 100, colorHex: props.foregroundHex, alphaPercent: 0 },
                ]);
              }}
            >
              前景→透明
            </button>
            <button
              type="button"
              class={styles.presetButton}
              data-testid="gradient-preset-black-white"
              onClick={() => {
                applyPreset(GRADIENT_PRESETS['blackToWhite']!);
              }}
            >
              黑→白
            </button>
          </div>

          <div class={styles.stopList}>
            {/* <Index>，不是 <For>：拖不透明度滑杆时 updateStop 每一帧都给这个
                色标造一个新对象引用——<For> 按引用比对，会把这判成"删了旧的、
                插了新的"，整行 DOM（含正在被原生拖拽的 <input type="range">）
                跟着重建，拖到一半指针捕获就丢了，表现成"只能点击不能拖拽"
                （跟本会话开头蚂蚁线导致菜单栏重建是同一类坑）。<Index> 按下标
                跟踪，值变了只是同一个 DOM 节点内部更新，不重建。 */}
            <Index each={stops()}>
              {(stop, index) => (
                <div class={styles.stopRow} data-testid={`gradient-stop-${index}`}>
                  <input
                    type="number"
                    class={styles.offsetInput}
                    min="0"
                    max="100"
                    value={stop().offsetPercent}
                    data-testid={`gradient-stop-offset-${index}`}
                    onInput={(event) => {
                      const v = Math.round(Number(event.currentTarget.value) || 0);
                      updateStop(index, { offsetPercent: Math.min(100, Math.max(0, v)) });
                    }}
                  />
                  <span>%</span>
                  <div class={styles.colorSwatch}>
                    <button
                      type="button"
                      class={styles.colorSwatchFill}
                      aria-label="打开取色器"
                      data-testid={`gradient-stop-color-${index}`}
                      style={{ 'background-color': stop().colorHex, opacity: stop().alphaPercent / 100 }}
                      onClick={() => {
                        setColorPickerIndex(index);
                      }}
                    />
                  </div>
                  <input
                    type="range"
                    class={styles.alphaInput}
                    min="0"
                    max="100"
                    value={stop().alphaPercent}
                    data-testid={`gradient-stop-alpha-${index}`}
                    onInput={(event) => {
                      updateStop(index, { alphaPercent: Number(event.currentTarget.value) });
                    }}
                  />
                  <span class={styles.alphaValue}>{stop().alphaPercent}%</span>
                  <button
                    type="button"
                    class={styles.removeButton}
                    disabled={stops().length <= 2}
                    data-testid={`gradient-stop-remove-${index}`}
                    onClick={() => {
                      removeStop(index);
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </Index>
          </div>

          <button type="button" class={styles.addButton} data-testid="gradient-stop-add" onClick={addStop}>
            + 添加色标
          </button>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="gradient-editor-confirm"
              onClick={() => {
                props.onApply(stops().map(fromEditable));
                props.onClose();
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
      <ColorPickerDialog
        open={colorPickerIndex() !== null}
        color={colorPickerIndex() !== null ? stops()[colorPickerIndex()!]!.colorHex : `#${'000000'}`}
        onClose={() => {
          setColorPickerIndex(null);
        }}
        onConfirm={(hex) => {
          if (colorPickerIndex() !== null) updateStop(colorPickerIndex()!, { colorHex: hex });
        }}
      />
      </>
    </Show>
  );
}
